import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, ApiError, describeApiError } from '../../api/authFetch';
import { adminColors, radius } from '../../theme/adminTheme';

/**
 * DocumentGenerationResult Component
 * 
 * A simplified UI component for displaying document generation results.
 * Shows only a clean message "Here is your Document" with a download link,
 * removing the complex code execution interface for a cleaner user experience.
 * 
 * Supports both presentations (PowerPoint) and documents (PDF/Word).
 */

const DocumentGenerationResult = ({ result }) => {
  const [downloadError, setDownloadError] = useState(null);

  if (!result) return null;

  // Handle both successful and failed document generation
  if (!result.success) {
    return (
      <Paper
        elevation={1}
        sx={{
          mt: 2,
          mb: 2,
          border: `1px solid ${adminColors.errorBorder}`,
          borderRadius: radius.surface,
          overflow: 'hidden'
        }}
      >
        <Box sx={{
          p: 3,
          backgroundColor: adminColors.errorLight,
          textAlign: 'center'
        }}>
          <Typography variant="body1" color="error" sx={{ fontWeight: 600 }}>
            {result.message || 'Document generation failed'}
          </Typography>
          {result.error && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {result.error}
            </Typography>
          )}
        </Box>
      </Paper>
    );
  }

  const { artifacts } = result;
  if (!artifacts || artifacts.length === 0) return null;

  const handleDownload = async (artifact) => {
    const { download_url, filename, type } = artifact;
    
    if (!download_url) return;

    setDownloadError(null);
    try {
      // Construct full URL with base URL
      const fullDownloadUrl = download_url.startsWith('http')
        ? download_url
        : `${config.API_BASE_URL}${download_url}`;

      // Set appropriate content type based on document type
      let acceptHeader = 'application/octet-stream';
      if (type === 'presentation') {
        acceptHeader = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      } else if (type === 'document') {
        if (artifact.format === 'pdf') {
          acceptHeader = 'application/pdf';
        } else if (artifact.format === 'docx') {
          acceptHeader = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        }
      }

      // Make authenticated request to download the file. authFetch (rather
      // than plain fetch) adds the Bearer fallback and a timeout, so a
      // stalled backend can't hang this download forever.
      const response = await authFetch(fullDownloadUrl, {
        method: 'GET',
        headers: {
          'Accept': acceptHeader
        }
      }, { timeoutMs: 60000 });

      if (response.status === 401) {
        window.location.href = '/signin';
        return;
      }

      if (!response.ok) {
        let detail;
        try {
          const data = await response.json();
          detail = data.detail;
        } catch (e) {
          // response body wasn't JSON; fall back to a generic message
        }
        throw new ApiError(response.status, detail);
      }

      // Get the file as a blob
      const blob = await response.blob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document.pptx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download error:', error);
      setDownloadError(describeApiError(error));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (artifact) => {
    if (artifact.type === 'presentation') {
      return `PowerPoint Presentation${artifact.slides_count ? ` (${artifact.slides_count} slides)` : ''}`;
    } else if (artifact.type === 'document') {
      if (artifact.format === 'pdf') {
        return `PDF Document${artifact.pages_count ? ` (${artifact.pages_count} pages)` : ''}`;
      } else if (artifact.format === 'docx') {
        return `Word Document${artifact.pages_count ? ` (${artifact.pages_count} pages)` : ''}`;
      }
      return `Document${artifact.pages_count ? ` (${artifact.pages_count} pages)` : ''}`;
    }
    return 'Document';
  };

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'rgba(55, 65, 81, 0.9)',
          lineHeight: 1.5
        }}
      >
        📄 Here is your document:{' '}
        {artifacts.map((artifact, index) => (
          <span key={index}>
            <Typography
              component="a"
              onClick={() => handleDownload(artifact)}
              sx={{
                color: adminColors.accent,
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  color: adminColors.accentDark
                }
              }}
            >
              {artifact.type === 'presentation' ? 'PowerPoint' : 
               artifact.format === 'pdf' ? 'PDF' :
               artifact.format === 'docx' ? 'Word Document' : 'Document'}
              {artifact.size && ` (${formatFileSize(artifact.size)})`}
            </Typography>
            {index < artifacts.length - 1 && ', '}
          </span>
        ))}
      </Typography>
      {downloadError && (
        <Alert severity="error" onClose={() => setDownloadError(null)} sx={{ mt: 1 }}>
          {downloadError}
        </Alert>
      )}
    </Box>
  );
};

export default DocumentGenerationResult;